import { useState } from "react";
import styles from "./FieldsArrayBuilder.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes, faGripVertical, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import SearchableDropdown from "../SearchableDropdown/SearchableDropdown";
import { useLanguage } from "../../context/LanguageContext";

interface FieldConfig {
  id?: string;
  field: string;
  name: string;
  relativeFrom?: string;
  relative?: string;
  value?: string;
  func?: string;
}

interface FieldsArrayBuilderProps {
  value: FieldConfig[];
  onChange: (fields: FieldConfig[]) => void;
  availableFields: Array<{ label: string; value: string }>;
  availableModules?: Array<{ label: string; value: string }>;
  title: string;
}

const FIELD_TYPES = [
  { value: "normal", label: "normalField" },
  { value: "relative", label: "relatedTable" },
  { value: "aggregate", label: "aggregateFunction" },
];

const AGGREGATE_FUNCTIONS = [
  "SUM",
  "COUNT",
  "AVG",
  "MAX",
  "MIN",
  "GROUP_CONCAT",
];

export default function FieldsArrayBuilder({
  value,
  onChange,
  availableFields,
  availableModules = [],
  title,
}: FieldsArrayBuilderProps) {
  const { t } = useLanguage();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const getFieldType = (field: FieldConfig): string => {
    if (field.func) return "aggregate";
    if (field.relative) return "relative";
    return "normal";
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newFields = [...value];
    const draggedItem = newFields[draggedIndex];
    newFields.splice(draggedIndex, 1);
    newFields.splice(index, 0, draggedItem);

    onChange(newFields);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleAdd = () => {
    const newField: FieldConfig = {
      id: `field_${Date.now()}`,
      field: availableFields[0]?.value || "",
      name: availableFields[0]?.label || "",
    };

    onChange([...value, newField]);
    setEditingIndex(value.length);
  };

  const handleRemove = (index: number) => {
    const newFields = value.filter((_, i) => i !== index);
    onChange(newFields);
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const handleFieldChange = (index: number, updates: Partial<FieldConfig>) => {
    const newFields = [...value];
    newFields[index] = { ...newFields[index], ...updates };
    onChange(newFields);
  };

  const handleTypeChange = (index: number, type: string) => {
    const field = value[index];
    const baseField: FieldConfig = {
      id: field.id,
      field: field.field,
      name: field.name,
    };

    if (type === "relative") {
      baseField.relativeFrom = "";
      baseField.relative = "";
      baseField.value = "";
    } else if (type === "aggregate") {
      baseField.func = `SUM("table"."${field.field}") as ${field.field}`;
    }

    handleFieldChange(index, baseField);
  };

  const toggleEdit = (index: number) => {
    setEditingIndex(editingIndex === index ? null : index);
  };

  const renderFieldPreview = (field: FieldConfig) => {
    const type = getFieldType(field);
    
    return (
      <div className={styles.fieldPreview}>
        <div className={styles.previewMain}>
          <span className={styles.fieldName}>{field.field}</span>
          <span className={styles.fieldLabelPreview}>→ {field.name}</span>
        </div>
        <div className={styles.previewMeta}>
          {type === "relative" && (
            <>
              <span className={styles.metaBadge}>
                {t('relation')}: {field.relative}
              </span>
              {field.relativeFrom && (
                <span className={styles.metaBadge}>
                  {t('from')}: {field.relativeFrom}
                </span>
              )}
              {field.value && (
                <span className={styles.metaBadge}>
                  {t('value')}: {field.value}
                </span>
              )}
            </>
          )}
          {type === "aggregate" && field.func && (
            <span className={styles.metaBadge}>
              {t('function')}: {field.func}
            </span>
          )}
          {type === "normal" && (
            <span className={styles.metaBadge}>{t('normalField')}</span>
          )}
        </div>
      </div>
    );
  };

  const renderFieldEditor = (field: FieldConfig, index: number) => {
    const type = getFieldType(field);

    const translatedFieldTypes = FIELD_TYPES.map(ft => ({
      value: ft.value,
      label: t(ft.label)
    }));

    return (
      <div className={styles.editForm}>
        <div className={styles.formRow}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t('fieldType')} *</label>
            <SearchableDropdown
              label=""
              options={translatedFieldTypes}
              value={type}
              onChange={(v) => handleTypeChange(index, v)}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t('fieldKey')} *</label>
            <SearchableDropdown
              label=""
              options={availableFields}
              value={field.field}
              onChange={(v) => handleFieldChange(index, { field: v })}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel}>{t('displayName')} *</label>
            <input
              type="text"
              className={styles.input}
              value={field.name || ""}
              onChange={(e) =>
                handleFieldChange(index, { name: e.target.value })
              }
              placeholder={t('columnName')}
            />
          </div>
        </div>

        {type === "relative" && (
          <>
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <label className={styles.fieldLabel}>{t('relativeFrom')}</label>
                <input
                  type="text"
                  className={styles.input}
                  value={field.relativeFrom || ""}
                  onChange={(e) =>
                    handleFieldChange(index, { relativeFrom: e.target.value })
                  }
                  placeholder={t('relativeFromPlaceholder')}
                />
              </div>

              <div className={styles.formField}>
                <label className={styles.fieldLabel}>{t('relativeTable')}</label>
                {availableModules.length > 0 ? (
                  <SearchableDropdown
                    label=""
                    options={availableModules}
                    value={field.relative || ""}
                    onChange={(v) => handleFieldChange(index, { relative: v })}
                  />
                ) : (
                  <input
                    type="text"
                    className={styles.input}
                    value={field.relative || ""}
                    onChange={(e) =>
                      handleFieldChange(index, { relative: e.target.value })
                    }
                    placeholder={t('relativeTablePlaceholder')}
                  />
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formField}>
                <label className={styles.fieldLabel}>{t('value')}</label>
                <input
                  type="text"
                  className={styles.input}
                  value={field.value || ""}
                  onChange={(e) =>
                    handleFieldChange(index, { value: e.target.value })
                  }
                  placeholder={t('valuePlaceholder')}
                />
              </div>
            </div>
          </>
        )}

        {type === "aggregate" && (
          <div className={styles.formRow}>
            <div className={styles.formField}>
              <label className={styles.fieldLabel}>{t('function')} *</label>
              <textarea
                className={styles.textarea}
                value={field.func || ""}
                onChange={(e) =>
                  handleFieldChange(index, { func: e.target.value })
                }
                placeholder={t('functionPlaceholder')}
                rows={3}
              />
              <div className={styles.funcHelp}>
                <span className={styles.funcHelpTitle}>{t('availableFunctions')}:</span>
                {AGGREGATE_FUNCTIONS.map((fn) => (
                  <button
                    key={fn}
                    type="button"
                    className={styles.funcBtn}
                    onClick={() => {
                      const funcTemplate = `${fn}("table"."${field.field}") as ${field.field}`;
                      handleFieldChange(index, { func: funcTemplate });
                    }}
                  >
                    {fn}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className={styles.jsonPreview}>
          <span className={styles.jsonLabel}>JSON:</span>
          <code className={styles.jsonCode}>
            {JSON.stringify(
              Object.fromEntries(
                Object.entries(field).filter(([key]) => key !== "id")
              ),
              null,
              2
            )}
          </code>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
        <button type="button" className={styles.addBtn} onClick={handleAdd}>
          <FontAwesomeIcon icon={faPlus} />
          {t('addField')}
        </button>
      </div>

      <div className={styles.fieldsList}>
        {value.length === 0 ? (
          <div className={styles.emptyState}>
            <p>{t('noFields')}</p>
          </div>
        ) : (
          value.map((field, index) => (
            <div
              key={field.id || index}
              className={`${styles.fieldCard} ${
                draggedIndex === index ? styles.dragging : ""
              } ${editingIndex === index ? styles.editing : ""}`}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className={styles.dragHandle}>
                <FontAwesomeIcon icon={faGripVertical} />
              </div>

              <div className={styles.fieldContent}>
                {editingIndex === index
                  ? renderFieldEditor(field, index)
                  : renderFieldPreview(field)}
              </div>

              <div className={styles.actions}>
                {editingIndex === index ? (
                  <>
                    <button
                      type="button"
                      className={styles.editBtn}
                      onClick={() => toggleEdit(index)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      type="button"
                      className={styles.closeBtn}
                      onClick={() => setEditingIndex(null)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className={styles.editBtn}
                      onClick={() => toggleEdit(index)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      type="button"
                      className={styles.deleteBtn}
                      onClick={() => handleRemove(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}